import { DatePipe } from '@angular/common';
import {
  Component,
  Inject,
  Input,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { datePickerValidator } from '@app/core/Directives/datepicker-validator';
import {
  AccountEntity,
  MaxLength,
  ModuleName,
  Modules,
  NotificationTextMessage,
  TransactionStatus,
} from '@app/core/Enum';
import {
  Currency,
  GlobalComponent,
  QuickJournalModel,
  SideListModel,
  VatRateScheme,
} from '@app/core/Models';
import { CommonService, HighlightRow } from '@app/core/Services';
import {
  CommonState,
  CreateFund,
  GetCurrencyList,
  GetCustomBankAccountList,
  GetDataByFundId,
  GetFundNameList,
  GetJournalAccounts,
  GetVatRateList,
  MenuState,
  QuickJournal,
} from '@app/core/Store';
import {
  ConfirmationBoxComponent,
  QuickAddComponent,
} from '@app/modules/common';
import { TransactionsAttachmentComponent } from '@app/modules/transactions';
import { Select, Store } from '@ngxs/store';
import { Guid } from 'guid-typescript';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, Subject, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-quick-journal',
  templateUrl: './quick-journal.component.html',
  styleUrls: ['./quick-journal.component.scss'],
})
export class QuickJournalComponent implements OnInit {
  quickJournalForm: FormGroup;
  receiptId = Guid.EMPTY as unknown as Guid;

  quickJournalData: QuickJournalModel;

  customerList: SideListModel[];
  accountTypeList: SideListModel[];
  accountList: any[] = [];
  currencyList: Currency[];
  receiptAccountList: any[] = [];
  tempReceiptAccountList: any[];
  getListId: Guid;
  fundNameList: SideListModel[];
  vatRateList: VatRateScheme[];

  @Input() bankId;

  accountGroupList: any[] = [];
  tempAccountGroupList: any[];

  subscriptionRouting: Subscription;
  moduleName = ModuleName;
  moduleId = Modules;
  disabledFundEditButton = true;

  amountReceivedValue = 0;
  amountReceivedTotal = 0;
  isAdvance = false;

  @Input()
  getModuleName?: string;

  @Input()
  getModuleId: number;

  @Select(MenuState.moduleId)
  moduleId$: Observable<number>;

  @Select(CommonState.defaultCurrency)
  defaultCurrency$: Observable<Guid>;

  @ViewChild(TransactionsAttachmentComponent, { static: true })
  attachmentDetail;

  transactionType = [
    {
      id: TransactionStatus.Receipt,
      name: TransactionStatus[TransactionStatus.Receipt],
    },
    {
      id: TransactionStatus.Payment,
      name: TransactionStatus[TransactionStatus.Payment],
    },
  ];

  accountLabelText: string;
  amountLabelText: string;

  defaultCurrency: Guid;
  periodicDate: any;

  isEdit = false;
  disabledEditButton = true;
  showReceiptDetails = false;

  notificationMessage = NotificationTextMessage;
  maxLength = MaxLength;
  accountingMehtodId: any;
  isVatRegistered: any;

  minInvoiceDate: Date;
  maxInvoiceDate: Date = new Date();

  private destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store,
    public commonService: CommonService,
    public highlightRow: HighlightRow,
    private globalComponent: GlobalComponent,
    private renderer: Renderer2,
    private spinner: NgxSpinnerService,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<ConfirmationBoxComponent>,
    private _Activatedroute: ActivatedRoute,
    public datepipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.isVatRegistered = this.globalComponent.getIsVatRegistered();
    this.bankId = atob(this.data.customId) as unknown as Guid;
    this.periodicDate = this.globalComponent.getFinancialPeriod();
    this.accountingMehtodId = this.globalComponent.getAccountingMethod();
    this.isEdit = false;
    this.setForm();
    this.loadDropdownValues();

    this.quickJournalForm?.valueChanges?.subscribe((value) => {
      this.commonService.isInitialValueChange = this.quickJournalForm.touched;
    });
    this.setInvoiceDateLimits();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setLabelText(): void {
    this.amountLabelText =
      this.quickJournalForm.controls.transactionType.value ===
      TransactionStatus.Payment
        ? 'Paid'
        : 'Received';

    this.accountLabelText =
      this.quickJournalForm.controls.transactionType.value ===
      TransactionStatus.Payment
        ? 'Payment'
        : 'Receipt';
  }

  bindCurrency(): void {
    this.defaultCurrency = this.globalComponent.getDefaultCurrency();
    this.store
      .dispatch(new GetCurrencyList())
      .pipe(
        tap(() => {
          this.currencyList = this.store.selectSnapshot(
            CommonState.getCurrency
          );
          if (this.currencyList.length > 0) {
            this.quickJournalForm.controls.currency.setValue(
              this.defaultCurrency
            );
          }
        })
      )
      .subscribe();
  }

  loadDropdownValues(): void {
    this.getAccounts();
    this.bindCurrency();
    this.getBankAccountList();
    this.getFundNameList();
    this.getVatRateList();
  }

  onTransactionTypeChange(): void {
    this.setLabelText();
  }

  getAccounts(): void {
    this.spinner.show();
    this.store.dispatch(new GetJournalAccounts()).subscribe((res) => {
      this.spinner.hide();

      this.accountGroupList = this.addGroupNameToListModels(
        res.account.accounts
      );
      this.accountGroupList = this.accountGroupList.filter(
        (x) => x.id !== this.bankId
      );
    });
  }

  addGroupNameToListModels(data: any[]): any[] {
    let result: any = [];

    data.forEach((group) => {
      group.listModels.forEach((listModel) => {
        let listModelWithGroup = {
          ...listModel,
          groupName: group.groupName,
        } as any;
        result.push(listModelWithGroup);
      });
    });

    return result;
  }

  getBankAccountList(): void {
    this.store
      .dispatch(new GetCustomBankAccountList())
      .pipe(
        tap((res) => {
          this.receiptAccountList = res.common.customBankAccount;
          this.tempReceiptAccountList = this.receiptAccountList;
          if (!this.commonService.isEmpty(this.bankId)) {
            this.quickJournalForm.controls.bankAccountId.setValue(this.bankId);
            this.quickJournalForm.controls.bankAccountId.disable();
          }
        })
      )
      .subscribe();
  }

  getVatRateList(): void {
    this.store.dispatch(new GetVatRateList()).subscribe((res) => {
      this.vatRateList = res.common.vatRate;
    });
  }

  calVatAmount(): void {
    let amount = 0;
    let netSalePrice = 0;
    let totalProfitLoss = 0;
    let vatRate = 0;
    if (this.vatRateList.length > 0) {
      this.vatRateList.forEach((element) => {
        if (element.id === this.quickJournalForm.controls.vat.value) {
          vatRate = element.rate;
        }
      });

      amount =
        +this.quickJournalForm.controls.amountReceived.value -
        (+this.quickJournalForm.controls.amountReceived.value * 100) /
          (100 + vatRate);

      this.quickJournalForm.controls.vatRate.setValue(+amount.toFixed(2));
    }
  }

  setForm(): void {
    this.quickJournalForm = new FormGroup(
      {
        accountTypeId: new FormControl(0),
        bankAccountId: new FormControl('', [Validators.required]),
        transactionType: new FormControl(TransactionStatus.Receipt, [
          Validators.required,
        ]),
        receiptDate: new FormControl(
          new Date(),
          datePickerValidator(this.periodicDate)
        ),
        currency: new FormControl({ value: '', disabled: true }, [
          Validators.required,
        ]),
        amountReceived: new FormControl('', [Validators.required]),
        note: new FormControl(''),
        accountId: new FormControl('', [Validators.required]),
        fundName: new FormControl('', [
          Validators.required,
          this.commonService.whiteSpaceValidate,
        ]),
        vat: new FormControl(''),
        vatRate: new FormControl({ value: 0, disabled: true }),
      },
      {
        validators: this.commonService.compareFields(
          'bankAccountId',
          'accoutListBank',
          this.getModuleId
        ),
      }
    );

    this.setLabelText();
  }

  accountGroupSelected(event: any, isEdit): void {
    const entityType = [AccountEntity.Customer, AccountEntity.Supplier];

    this.showReceiptDetails = entityType.includes(
      isEdit ? event.accountTypeId : +event.option.value.accountTypeId
    );

    if (!isEdit && this.showReceiptDetails) {
      this.quickJournalForm.controls.customerName.setValue(
        event.option.value.id
      );
      this.onCustomerChange('');
    }

    if (this.getModuleId === Modules.BankEntry) {
      this.receiptAccountList = this.tempReceiptAccountList.filter(
        (i) => i.id !== event.id
      );
    }
  }

  onCustomerChange(event: any): void {
    if (event !== null && event !== undefined) {
      this.disabledEditButton = false;
    } else {
      this.disabledEditButton = true;
    }
  }

  getCustomBankAccountList(): void {
    this.store
      .dispatch(new GetCustomBankAccountList())
      .pipe(
        tap((res) => {
          this.accountList = res.common.accountList;

          if (this.accountList.length > 0) {
            this.quickJournalForm.controls.accountId.setValue(
              this.accountList[0].id
            );
          } else {
            this.quickJournalForm.controls.accountId.reset();
          }
        })
      )
      .subscribe();
  }

  onCheckBoxSelected(event: any): void {
    this.isAdvance = event.checked;
    this.showReceiptDetails = !event.checked;
  }

  onCloseClick(): void {
    this.dialogRef.close();
  }

  getFundNameList(): void {
    this.store
      .dispatch(new GetFundNameList())
      .pipe(
        tap((res) => {
          this.fundNameList = res.account.fundNameList;
          if (this.fundNameList.length > 0) {
            this.disabledFundEditButton = false;
            this.quickJournalForm.controls.fundName.setValue(
              this.fundNameList[0].id
            );
          }
        })
      )
      .subscribe();
  }

  fundChange(event: any): void {
    if (event !== null && event !== undefined) {
      this.disabledFundEditButton = false;
    } else {
      this.disabledFundEditButton = true;
    }
  }

  gotoAddFund(): void {
    this.dialog
      .open(QuickAddComponent, {
        data: {
          moduleId: Modules.FundList,
          headerText: `Add ${ModuleName.Fund}`,
          saveActionName: CreateFund,
        },
      })
      .afterClosed()
      .subscribe((id) => {
        if (!this.commonService.isEmpty(id)) {
          this.disabledFundEditButton = false;
          this.getFund(id);
        }
      });
  }

  getFund(id): void {
    this.store.dispatch(new GetFundNameList()).subscribe((res) => {
      this.fundNameList = res.account.fundNameList;
      if (id !== null && id !== undefined) {
        this.quickJournalForm.controls.fundName.setValue(id);
      }
    });
  }

  gotoEditFund(): void {
    if (this.fundNameList.length > 0) {
      this.dialog
        .open(QuickAddComponent, {
          data: {
            moduleId: Modules.FundList,
            headerText: `Edit ${ModuleName.Fund}`,
            id: this.quickJournalForm.controls.fundName.value,
            saveActionName: CreateFund,
            getActionName: GetDataByFundId,
          },
        })
        .afterClosed()
        .subscribe((id) => {
          if (!this.commonService.isEmpty(id)) {
            this.getFund(id);
          }
        });
    }
  }

  onCancel(): void {
    this.ngOnInit();
  }

  dataSubmit(): boolean {
    this.spinner.show();
    try {
      const filedata: any[] = [];

      if (
        this.attachmentDetail.fileUploadResponse.length > 0 &&
        this.attachmentDetail.fileUploadResponse !== undefined &&
        this.attachmentDetail.fileUploadResponse !== null
      ) {
        this.attachmentDetail.fileUploadResponse.forEach((element) => {
          filedata.push(element.fileUrl);
        });
      }

      this.quickJournalData = {
        entryDate: this.datepipe
          .transform(
            this.quickJournalForm.controls.receiptDate.value,
            'yyyy-MM-dd'
          )
          ?.toString(),
        fromAccountId: this.quickJournalForm.controls.bankAccountId.value,
        toAccountId: this.quickJournalForm.controls.accountId.value,
        fundId: this.quickJournalForm.controls.fundName.value,
        currencyId: this.quickJournalForm.controls.currency.value,
        vatRateId:
          this.quickJournalForm.controls.vat.value === ''
            ? null
            : this.quickJournalForm.controls.vat.value,
        vatAmount: this.quickJournalForm.controls.vatRate.value,
        totalAmount: this.quickJournalForm.controls.amountReceived.value,
        note: this.quickJournalForm.controls.note.value,
        isCredit:
          this.quickJournalForm.controls.transactionType.value > 0
            ? true
            : false,
        attachment: filedata,
      };
    } catch (error) {
      this.spinner.hide();
      this.commonService.onFailure(NotificationTextMessage.errorMessage);
      return false;
    }
    return true;
  }

  onSave(isExit: boolean): void {
    if (this.quickJournalForm.invalid) {
      this.quickJournalForm.markAllAsTouched();
      return;
    } else {
      if (this.dataSubmit()) {
        if (this.quickJournalData) {
          this.store
            .dispatch(new QuickJournal(this.quickJournalData))
            .pipe()
            .subscribe(
              (res) => {
                if (res !== undefined) {
                  if (isExit) {
                    this.commonService.onSucess(
                      NotificationTextMessage.successMessage
                    );
                    this.dialogRef.close();
                  } else {
                    this.onCancel();
                  }
                } else {
                  this.commonService.onFailure(
                    NotificationTextMessage.errorMessage
                  );
                }
              },
              (err) => {
                this.commonService.onFailure(err.error.Message);
              }
            );
        }
      }
    }
  }

  setInvoiceDateLimits(): void {
    if (this.periodicDate?.length > 0) {
      const unlockedPeriod = this.periodicDate.find(
        (period: any) => !period.isLocked
      );

      if (unlockedPeriod) {
        this.minInvoiceDate = new Date(unlockedPeriod.fromDate);
      } else {
        this.minInvoiceDate = new Date(this.periodicDate[0].fromDate);
      }

      this.maxInvoiceDate = new Date(
        this.periodicDate[this.periodicDate.length - 1]?.toDate
      );
    }
  }
}
